import React, { useState } from "react";

import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";

import { apiBaseUrls } from "@platform/constants/shared-constants";

interface RushaIdpButtonProps {
  disabled?: boolean;
  label?: string;
  organizationId?: number;
  mode?: "login" | "register";
}

export const buildRushaIdpUrl = (
  organizationId?: number,
  mode: "login" | "register" = "login",
): string => {
  const params = new URLSearchParams();
  if (mode === "register") {
    params.set("mode", mode);
  }
  if (organizationId !== undefined) {
    params.set("organizationId", String(organizationId));
  }
  const query = params.toString();
  return `${apiBaseUrls.BACKEND}/api/v1/auth/oidc/start${query ? `?${query}` : ""}`;
};

const RushaIdpButton: React.FC<RushaIdpButtonProps> = ({
  disabled = false,
  label = "Continue with Rusha",
  organizationId,
  mode = "login",
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const [error, setError] = useState<string | null>(null);
  const [launching, setLaunching] = useState(false);

  const startOidc = () => {
    setError(null);
    setLaunching(true);
    window.location.assign(buildRushaIdpUrl(organizationId, mode));
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1,
        mt: 1.5,
        p: 1.5,
        borderLeft: `3px solid ${theme.palette.primary.main}`,
        borderRadius: "0 6px 6px 0",
        bgcolor: alpha(theme.palette.info.main, isDark ? 0.12 : 0.07),
      }}
    >
      <Typography
        variant="body2"
        sx={{ color: "text.primary", fontWeight: 600 }}
      >
        {organizationId === undefined
          ? mode === "register"
            ? "Create your Rusha account"
            : "Use your Rusha account"
          : "Use your organization identity"}
      </Typography>
      <Typography
        variant="caption"
        sx={{ color: "text.secondary", lineHeight: 1.5 }}
      >
        {organizationId === undefined
          ? mode === "register"
            ? "Create your native Rusha account through Keycloak. You can create or join an organization after authentication."
            : "Create or access your native Rusha account through Keycloak. You can create or join an organization after authentication."
          : "Continue through the active Keycloak broker for this organization. Membership and Rusha permissions remain managed by Rusha."}
      </Typography>
      {error && (
        <Alert
          severity="error"
          role="alert"
          sx={{ py: 0, borderRadius: "6px", fontSize: "12px" }}
        >
          {error}
        </Alert>
      )}
      <Button
        type="button"
        variant="outlined"
        onClick={startOidc}
        disabled={disabled || launching}
        startIcon={<ShieldOutlinedIcon />}
        sx={{
          minHeight: "40px",
          borderRadius: "10px",
          textTransform: "none",
          fontWeight: 600,
          color: "text.primary",
          borderColor: isDark
            ? theme.palette.grey[600]
            : theme.palette.grey[300],
          "&:hover": {
            borderColor: theme.palette.primary.main,
            bgcolor: alpha(theme.palette.primary.main, isDark ? 0.12 : 0.05),
          },
          "&.Mui-focusVisible": {
            boxShadow: `0 0 0 2px ${theme.palette.background.paper}, 0 0 0 4px ${theme.palette.primary.main}`,
          },
        }}
      >
        {launching ? (
          <>
            <CircularProgress
              size={16}
              color="inherit"
              aria-label="Redirecting"
            />
            Redirecting...
          </>
        ) : (
          label
        )}
      </Button>
    </Box>
  );
};

export default RushaIdpButton;
